// Pure edge/firehose market-frame admission truth table, extracted from
// marketStore.feedShouldIngest (#441). The store computes the live facts
// (fleet lookup, edge announce, per-venue delivery TTL) and applies the
// venue-fallback side effects; this module only decides.
export type MarketFeedRole = 'firehose' | 'edge';

export interface MarketFrameAdmissionInput {
  /** Null means the fleet entry disappeared after callbacks were created. */
  feedRole: MarketFeedRole | null;
  /** Result of the feed's build-time/static `accepts(sport)` predicate. */
  staticallyAccepts: boolean;
  /** A connected edge currently announces this sport. */
  edgeServing: boolean;
  /** Both normalized sport and venue are present for venue-level arbitration. */
  hasSportAndVenue: boolean;
  /** The serving edge delivered this (sport, venue) within the existing TTL. */
  edgeDeliveringVenue: boolean;
  /** A DIFFERENT claimant currently owns this sport. Only meaningful for a
   *  claimant that statically accepts it.
   *
   *  Two claimants accepting one sport is double-ingest, not redundancy. It
   *  could not arise while scopes came only from env — no two edges were ever
   *  configured for the same sport — but a core assignment can widen one edge
   *  onto a sport another already claims, so the owner arbitrates. Optional,
   *  and absent means "no contest", which keeps every pre-existing caller and
   *  truth-table case behaving exactly as before. */
  ownedByOtherClaimant?: boolean;
  /** D10 FLOOR (rule 6 as amended; Dan 2026-08-18): this feed is a CORE
   *  whose scope does NOT accept the sport, the sport has no serving owner
   *  anywhere (no edge serving, firehose not announcing), and THIS core is
   *  the first announcing floor core in fleet order. The store computes
   *  every fact; true admits the thin-firehose frame as the universal last
   *  resort. Optional and false-by-default so every pre-floor caller and
   *  truth-table case behaves exactly as before. */
  coreFloorEligible?: boolean;
}

export type MarketFrameAdmissionReason =
  | 'unknown-feed'
  | 'edge-static-scope'
  | 'edge-out-of-scope'
  | 'core-floor'
  | 'edge-not-owner'
  | 'firehose-static-scope'
  | 'firehose-no-serving-edge'
  | 'firehose-edge-sport-owner'
  | 'firehose-edge-venue-owner'
  | 'firehose-venue-fallback';

export interface MarketFrameAdmissionDecision {
  admit: boolean;
  reason: MarketFrameAdmissionReason;
}

export function decideMarketFrameAdmission(
  input: MarketFrameAdmissionInput,
): MarketFrameAdmissionDecision {
  if (input.feedRole == null) return { admit: true, reason: 'unknown-feed' };
  if (input.feedRole === 'edge') {
    if (!input.staticallyAccepts) {
      // The floor admits out-of-scope CORE frames only when nobody else
      // serves the sport — structurally unable to preempt, because the
      // store recomputes eligibility per frame and any owner's presence
      // makes it false.
      if (input.coreFloorEligible) return { admit: true, reason: 'core-floor' };
      return { admit: false, reason: 'edge-out-of-scope' };
    }
    // Permissive when nobody owns it yet: a claimant that accepts but has not
    // announced still ingests, exactly as before.
    if (input.ownedByOtherClaimant) return { admit: false, reason: 'edge-not-owner' };
    return { admit: true, reason: 'edge-static-scope' };
  }
  if (input.staticallyAccepts) return { admit: true, reason: 'firehose-static-scope' };
  if (!input.edgeServing) return { admit: true, reason: 'firehose-no-serving-edge' };
  if (!input.hasSportAndVenue) return { admit: false, reason: 'firehose-edge-sport-owner' };
  if (input.edgeDeliveringVenue) return { admit: false, reason: 'firehose-edge-venue-owner' };
  return { admit: true, reason: 'firehose-venue-fallback' };
}
